/**
 * Impersonation API service — manages admin impersonation lifecycle.
 */

import api from './client';

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: string;
  permissions: string[];
  isActive: boolean;
  authProvider: string;
  createdAt: string;
  _count: {
    connections: number;
    dataModels: number;
    dashboards: number;
  };
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

export interface StartImpersonationResponse {
  accessToken: string;
  impersonationSessionId: string;
  targetUser: {
    id: string;
    email: string;
    name: string | null;
  };
}

export const impersonationService = {
  /**
   * List all users (admin only)
   */
  listUsers: async (page = 1, limit = 50, search?: string): Promise<AdminUsersResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return api.get<AdminUsersResponse>(`/admin/users?${params.toString()}`);
  },

  updateUserStatus: async (userId: string, isActive: boolean): Promise<AdminUser> =>
    api.patch(`/admin/users/${encodeURIComponent(userId)}/status`, { isActive }),

  /**
   * Start impersonating a user
   */
  startImpersonation: async (userId: string, reason?: string): Promise<StartImpersonationResponse> => {
    return api.post<StartImpersonationResponse>('/admin/impersonation', { userId, reason });
  },

  /**
   * End the current impersonation session
   */
  endImpersonation: async (): Promise<void> => {
    await api.post('/admin/impersonation/end');
  },
};

export default impersonationService;